import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import ComputerIcon from '@mui/icons-material/Computer';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PeopleIcon from '@mui/icons-material/People';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Button, Container, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import LogoSvg from '../../../../assets/socotra-white-horiz.svg';
import { AuthData } from '../../../../context/auth';
import UserContext from '../../../../context/userContext';
import { HeaderUserDropdown } from '../../tenant/components';
import {
	ACCOUNT_BUTTON_LABEL,
	LOGOUT_ACTION_TEXT,
	SWITCH_ROLES_BUTTON_LABEL,
} from '../../tenant/data/constants';
import { UsersApi } from '../api';

const drawerWidth = 240;
const headerHeight = 64;

interface Props {
	authData: AuthData;
	error: any;
	children?: React.ReactNode;
}

interface DropdownItem {
	label: string;
	icon?: JSX.Element;
	callback?: () => void;
}

export default function ResponsiveDrawer({ authData, error, children }: Props) {
	const { userRoles, changeRole, user } = React.useContext(UserContext);
	const USER_KEY = UsersApi.USERS_KEY + '-' + user.activeNamespace;

	const {
		data: { firstName, lastName },
	} = useQuery({
		queryKey: [USER_KEY, user.userUuid],
		queryFn: () => UsersApi.getUser(user.userUuid),
		initialData: user,
	});

	const [confirmLogout, setConfirmLogout] = React.useState(false);
	const location = useLocation();

	const hasMultipleRoles = userRoles.length > 1;

	const linksArr: DropdownItem[] = [
		{
			label: ACCOUNT_BUTTON_LABEL(),
			icon: <AccountCircleOutlinedIcon />,
			callback: () => '/admin/account',
		},
	];

	const actionsArr: DropdownItem[] = [
		{
			label: LOGOUT_ACTION_TEXT(),
			icon: <ExitToAppIcon />,
			callback: () => setConfirmLogout(true),
		},
	];

	const userRolesArr: DropdownItem[] = hasMultipleRoles
		? [
				{
					label: SWITCH_ROLES_BUTTON_LABEL(),
					icon: <PersonOutlineIcon />,
					callback: changeRole,
				},
		  ]
		: [];

	return (
		<Box id='AdminPortal' height='100vh'>
			<CssBaseline />
			{/* Top navbar */}
			<Box
				id='AppBar'
				sx={{
					backgroundColor: 'background.default',
					color: 'common.black',
					display: 'flex',
					height: '64px',
					width: '100%',
				}}
			>
				<Box
					id='Sidebar__Button--Logo'
					component={NavLink}
					to='/admin/users'
					sx={{
						backgroundColor: 'primary.dark',
						height: '100%',
						flex: `0 0 ${drawerWidth}px`,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<LogoSvg />
				</Box>
				<Box flex={1} />
				<HeaderUserDropdown
					links={[...linksArr]}
					actions={[...userRolesArr, ...actionsArr]}
				>
					{firstName + ' ' + lastName}
				</HeaderUserDropdown>
			</Box>

			<Box id='MainContent' sx={{ height: `calc(100vh - ${headerHeight}px)` }}>
				<Box display='flex' height='100%'>
					{/* Sidebar */}
					<Stack
						key='drawer-links'
						id='Sidebar'
						sx={{
							height: '100%',
							backgroundColor: 'primary.dark',
							color: 'white',
							boxSizing: 'border-box',
							flex: `0 0 ${drawerWidth}px`,
						}}
					>
						<List sx={{ flexGrow: 1 }}>
							<List id='Sidebar__List'>
								<ListItemButton
									selected={location.pathname.includes('/admin/users')}
									component={NavLink}
									to='/admin/users'
								>
									<ListItemIcon>
										<PeopleIcon
											sx={{
												color: location.pathname.includes('/admin/users')
													? 'secondary.main'
													: 'white.lightest',
											}}
										/>
									</ListItemIcon>
									<ListItemText
										sx={{ color: 'white.lightest' }}
										primary='Users'
									/>
								</ListItemButton>
								<ListItemButton
									selected={location.pathname.includes('/admin/clients')}
									component={NavLink}
									to='/admin/clients'
								>
									<ListItemIcon>
										<ComputerIcon
											sx={{
												color: location.pathname.includes('/admin/clients')
													? 'secondary.main'
													: 'white.lightest',
											}}
										/>
									</ListItemIcon>
									<ListItemText
										sx={{ color: 'white.lightest' }}
										primary='Clients'
									/>
								</ListItemButton>
								{authData.ssoConfig.ssoMigrationEnabled && (
									<ListItemButton
										selected={location.pathname.includes('/admin/migrate')}
										component={NavLink}
										to='/admin/migrate'
									>
										<ListItemIcon>
											<SyncAltIcon
												sx={{
													color: location.pathname.includes('/admin/migrate')
														? 'secondary.main'
														: 'white.lightest',
												}}
											/>
										</ListItemIcon>
										<ListItemText
											sx={{ color: 'white.lightest' }}
											primary='Migrate'
										/>
									</ListItemButton>
								)}
								<ListItemButton
									selected={location.pathname.includes('/admin/tenants')}
									component={NavLink}
									to='/admin/tenants'
								>
									<ListItemIcon>
										<BusinessIcon
											sx={{
												color: location.pathname.includes('/admin/tenants')
													? 'secondary.main'
													: 'white.lightest',
											}}
										/>
									</ListItemIcon>
									<ListItemText
										sx={{ color: 'white.lightest' }}
										primary='Tenants'
									/>
								</ListItemButton>
							</List>
						</List>
					</Stack>
					{/* Main content */}
					<Box
						component='main'
						sx={{
							backgroundColor: 'background.default',
							borderTop: '1px solid',
							borderColor: 'divider',
							flex: 1,
						}}
					>
						<Container
							maxWidth='xl'
							sx={{
								pt: 3,
								pb: 3,
								height: '100%',
							}}
						>
							<Outlet />
							{children}
						</Container>
					</Box>
				</Box>
				{/* Dialogs */}
				<Dialog open={error}>
					<DialogTitle>Session Expired</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Your session has expired. Please log in again.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button color='secondary' onClick={() => authData.logout()}>
							Continue
						</Button>
					</DialogActions>
				</Dialog>
				<Dialog open={confirmLogout}>
					<DialogTitle>Confirm Logout</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Are you sure you want to logout?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setConfirmLogout(false)}>Cancel</Button>
						<Button color='secondary' onClick={() => authData.logout()}>
							Logout
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</Box>
	);
}
